import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, ScrollView, StatusBar, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { doc, onSnapshot } from 'firebase/firestore';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Colors, Radius, Shadow, Typography } from '../theme';
import { auth, firestore, functionsEu, httpsCallable } from '../firebase';
import type { RootStackParamList } from '../types';
import { useI18n } from '../i18n';

type Row = { icon: string; title: string; subtitle: string; onPress: () => void };
type CalendarConnection = {
  calendarName?: string;
  status?: string;
  paused?: boolean;
  lastSuccessfulSyncAt?: { toDate?: () => Date };
  lastError?: string;
};

export default function PartnerHomeScreen() {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { t } = useI18n();
  const uid = auth.currentUser?.uid ?? '';
  const [calendar, setCalendar] = useState<CalendarConnection | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!uid) return undefined;
    return onSnapshot(doc(firestore, 'partnerCalendarIntegrations', uid), snapshot => {
      setCalendar(snapshot.exists() ? snapshot.data() as CalendarConnection : null);
    });
  }, [uid]);

  const syncCalendar = async () => {
    setSyncing(true);
    try {
      const sync = httpsCallable(functionsEu, 'syncPartnerCalendarEu');
      await sync({ ownerUid: uid });
      Alert.alert('Calendar synced', 'Your imported drafts are up to date.');
    } catch (error) {
      Alert.alert('Sync failed', error instanceof Error ? error.message : 'Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const rows: Row[] = [
    {
      icon: 'user',
      title: t('partner.profile'),
      subtitle: t('partner.profileSub'),
      onPress: () => navigation.navigate('PartnerAccountProfile'),
    },
    {
      icon: 'plus-circle',
      title: t('partner.createEvent'),
      subtitle: t('partner.createEventSub'),
      onPress: () => navigation.navigate('UploadEvent'),
    },
    {
      icon: 'grid',
      title: t('partnerCalendar.title'),
      subtitle: t('partnerCalendar.entrySub'),
      onPress: () => navigation.navigate('PartnerCalendar'),
    },
    {
      icon: 'calendar',
      title: t('partner.myEvents'),
      subtitle: t('partner.myEventsSub'),
      onPress: () => navigation.navigate('CalendarImports'),
    },
    {
      icon: 'bar-chart-2',
      title: t('partner.analytics'),
      subtitle: t('partner.analyticsSub'),
      onPress: () => navigation.navigate('PartnerAnalytics'),
    },
    {
      icon: 'bell',
      title: t('partner.notifications'),
      subtitle: t('partner.notificationsSub'),
      onPress: () => navigation.navigate('PartnerNotifications'),
    },
  ];

  return (
    <LinearGradient colors={['#0B1826', '#070B14', '#020409']} style={styles.container}>
      <StatusBar barStyle="light-content" translucent backgroundColor="transparent" />
      <ScrollView contentContainerStyle={[styles.scroll, { paddingTop: insets.top + 24, paddingBottom: insets.bottom + 40 }]}>
        <Text style={styles.kicker}>{t('partner.kicker')}</Text>
        <Text style={styles.title}>{t('partner.tools')}</Text>

        {rows.map(row => (
          <TouchableOpacity key={row.title} style={styles.row} activeOpacity={0.85} onPress={row.onPress}>
            <View style={styles.rowIcon}>
              <Icon name={row.icon} size={18} color="#FF7A00" />
            </View>
            <View style={styles.rowText}>
              <Text style={styles.rowTitle}>{row.title}</Text>
              <Text style={styles.rowSubtitle}>{row.subtitle}</Text>
            </View>
            <Icon name="chevron-right" size={18} color={Colors.textMuted} />
          </TouchableOpacity>
        ))}

        {calendar ? (
          <View style={styles.calendarCard}>
            <View style={styles.calendarHeader}>
              <View style={styles.rowIcon}><Icon name="calendar" size={18} color="#FF7A00" /></View>
              <View style={styles.rowText}>
                <Text style={styles.rowTitle}>{calendar.calendarName || 'Luma calendar'}</Text>
                <Text style={styles.rowSubtitle}>Status: {calendar.paused ? 'paused' : (calendar.status || 'connected')}</Text>
              </View>
            </View>
            <Text style={styles.calendarMeta}>
              Last successful sync: {calendar.lastSuccessfulSyncAt?.toDate?.().toLocaleString('en-GB', { timeZone: 'Europe/Berlin' }) || 'Never'}
            </Text>
            {calendar.lastError ? <Text style={styles.calendarError}>{calendar.lastError}</Text> : null}
            <View style={styles.calendarActions}>
              <TouchableOpacity disabled={syncing || calendar.paused} style={[styles.calendarButton, (syncing || calendar.paused) && styles.disabled]} onPress={() => void syncCalendar()}>
                {syncing ? <ActivityIndicator size="small" color="#fff" /> : <Text style={styles.calendarButtonText}>Sync now</Text>}
              </TouchableOpacity>
              <TouchableOpacity disabled style={[styles.calendarButton, styles.pauseButton, styles.disabled]} accessibilityHint="Only a Tonyte admin can pause this connection">
                <Text style={styles.calendarButtonText}>Pause sync</Text>
              </TouchableOpacity>
            </View>
            <Text style={styles.adminHint}>Connection and pause controls are managed by Tonyte Admin.</Text>
          </View>
        ) : null}
      </ScrollView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.bg },
  scroll: { paddingHorizontal: 20 },
  kicker: {
    color: '#FF7A00',
    fontSize: Typography.xs,
    fontWeight: Typography.extrabold,
    letterSpacing: 2,
    textTransform: 'uppercase',
  },
  title: {
    marginTop: 4,
    marginBottom: 20,
    color: Colors.textPrimary,
    fontSize: Typography.xxl,
    fontWeight: Typography.extrabold,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(9,17,29,0.92)',
    marginBottom: 10,
    ...Shadow.strong,
  },
  rowIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,122,0,0.12)',
    marginRight: 12,
  },
  rowText: { flex: 1 },
  rowTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.semibold,
  },
  rowSubtitle: {
    marginTop: 2,
    color: Colors.textMuted,
    fontSize: Typography.xs,
  },
  calendarCard: { marginTop: 14, padding: 16, borderRadius: Radius.lg, borderWidth: 1, borderColor: Colors.border, backgroundColor: 'rgba(9,17,29,0.92)' },
  calendarHeader: { flexDirection: 'row', alignItems: 'center' },
  calendarMeta: { marginTop: 12, color: Colors.textMuted, fontSize: Typography.xs },
  calendarError: { marginTop: 8, color: '#FF8A80', fontSize: Typography.xs },
  calendarActions: { flexDirection: 'row', gap: 10, marginTop: 14 },
  calendarButton: { flex: 1, minHeight: 42, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: '#FF7A00' },
  pauseButton: { backgroundColor: '#46505C' },
  disabled: { opacity: 0.5 },
  calendarButtonText: { color: '#fff', fontWeight: Typography.bold, fontSize: Typography.sm },
  adminHint: { marginTop: 8, color: Colors.textMuted, fontSize: 10 },
});
